import { useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Tooltip,
  Typography,
} from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";

import { getPublicShipmentExportPDFRenderConfig } from "./utils";
import { usePublicShipmentPDFExport } from "../../hooks/usePublicShipmentPDFExport";

interface Props {
  open: boolean;
  onClose: () => void;
  shipment: PublicShipment;
}

export function PublicShipmentExportToPDFModal(props: Props) {
  const { open, onClose, shipment } = props;
  const [includeCarrierInfo, setIncludeCarrierInfo] = useState<boolean>(false);
  const { exportShipmentToPDF, isExporting, error } =
    usePublicShipmentPDFExport({ shipment });

  const carrierInfoAvailable =
    !!shipment.info.booked_at && !!shipment.booked_carrier;
  const optionsTextColor = carrierInfoAvailable
    ? "text.primary"
    : "text.secondary";

  const handleClose = () => {
    setIncludeCarrierInfo(false);
    onClose();
  };

  const handleExport = async () => {
    const sections = getPublicShipmentExportPDFRenderConfig(
      shipment,
      includeCarrierInfo,
    );
    try {
      await exportShipmentToPDF(sections);
      handleClose();
    } catch {
      // Rejection already surfaced via ErrorTypography from the export hook.
    }
  };

  return (
    <Box>
      <Dialog fullWidth open={open} onClose={handleClose}>
        <DialogTitle>Export shipment to PDF</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This will export the shipment details and route information to a PDF
            file.
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={includeCarrierInfo}
                onChange={(e) => setIncludeCarrierInfo(e.target.checked)}
                disabled={!carrierInfoAvailable}
                size="medium"
              />
            }
            label={
              <Typography variant="h6" color={optionsTextColor}>
                Include carrier information in export
              </Typography>
            }
          />

          <ErrorTypography error={error} />
        </DialogContent>
        <LoadingWrappedNode loading={isExporting}>
          <DialogActions>
            <Tooltip title="Close without downloading">
              <Button onClick={handleClose} sx={{ textTransform: "none" }}>
                Cancel
              </Button>
            </Tooltip>
            <Tooltip title="Download this shipment as a PDF">
              <Button
                onClick={handleExport}
                autoFocus
                color="primary"
                variant="contained"
                sx={{ textTransform: "none" }}
              >
                Download PDF
              </Button>
            </Tooltip>
          </DialogActions>
        </LoadingWrappedNode>
      </Dialog>
    </Box>
  );
}
